import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Settings, Warehouse, RefreshCw, Lock, ArrowRightLeft, AlertTriangle,
  ClipboardCheck, Timer, TrendingUp, PackageCheck, Eye, Shield,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import { getBrandCurrencySuffix } from '../../shared/utils/brandCurrency';
import ModuleConfigPageLayout from '../../shared/components/settings/ModuleConfigPageLayout';
import { DEFAULT_INVENTORY_CONFIG, normalizeInventoryConfig } from './inventaireConfigDefaults';
import { syncInventoryConfigToSettings } from './useInventoryConfig';

const NS = 'modules.inventaire.pages.inventoryConfigPage';

function ConfigSection({ icon: Icon, title, hint, children }) {
  return (
    <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
      <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
        <Icon className="h-6 w-6 shrink-0" />
        {title}
      </h2>
      {hint ? <p className="text-white/50 text-sm mb-4 text-right">{hint}</p> : <div className="mb-4" />}
      {children}
    </div>
  );
}

function ConfigCheckbox({ checked, onChange, label }) {
  return (
    <div className="flex items-center">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
      />
      <label className="ml-3 text-white/80">{label}</label>
    </div>
  );
}

function ConfigField({ label, hint, children }) {
  return (
    <div>
      <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{label}</label>
      {children}
      {hint ? <p className="text-white/50 text-xs mt-1 text-right">{hint}</p> : null}
    </div>
  );
}

const inputClass = 'w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500';

const InventoryConfigPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const qawaem = isSaudiBrand();
  const currencySuffix = getBrandCurrencySuffix();
  const { patchSettings, settings } = useSettings();
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const [inventoryConfig, setInventoryConfig] = useState(DEFAULT_INVENTORY_CONFIG);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('warehouses');

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await localApiAdapter.get('/inventory/config');
      const raw = response?.data !== undefined ? response.data : response;
      const merged = normalizeInventoryConfig(raw || {});
      setInventoryConfig(merged);
      syncInventoryConfigToSettings(merged, patchSettings, settingsRef.current);
    } catch (error) {
      console.error('Error loading inventory config:', error);
      const fallback = normalizeInventoryConfig({});
      setInventoryConfig(fallback);
      syncInventoryConfigToSettings(fallback, patchSettings, settingsRef.current);
    } finally {
      setLoading(false);
    }
  }, [patchSettings]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const payload = normalizeInventoryConfig(inventoryConfig);
      await localApiAdapter.put('/inventory/config', payload);
      syncInventoryConfigToSettings(payload, patchSettings, settingsRef.current);
      setInventoryConfig(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving inventory config:', error);
      setSaveError(p('save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(p('reset_confirm'))) return;
    try {
      const resetConfig = normalizeInventoryConfig({});
      setInventoryConfig(resetConfig);
      await localApiAdapter.put('/inventory/config', resetConfig);
      syncInventoryConfigToSettings(resetConfig, patchSettings, settingsRef.current);
    } catch (error) {
      console.error('Error resetting inventory config:', error);
      window.alert(p('reset_error'));
    }
  };

  const updateConfig = (key, value) => {
    setInventoryConfig((prev) => {
      const next = normalizeInventoryConfig({ ...prev, [key]: value });
      syncInventoryConfigToSettings(next, patchSettings, settingsRef.current);
      return next;
    });
  };

  const prefixField = (prefixKey, labelKey, hintKey, autoKey, autoLabelKey) => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <ConfigField label={p(labelKey)} hint={p(hintKey)}>
        <input
          type="text"
          value={inventoryConfig[prefixKey]}
          onChange={(e) => updateConfig(prefixKey, e.target.value.toUpperCase())}
          className={inputClass}
        />
      </ConfigField>
      <div className="flex items-center">
        <ConfigCheckbox
          checked={inventoryConfig[autoKey]}
          onChange={(v) => updateConfig(autoKey, v)}
          label={p(autoLabelKey)}
        />
      </div>
    </div>
  );

  const warehousesTab = (
    <ConfigSection icon={Warehouse} title={p('section_warehouses')} hint={p('section_warehouses_hint')}>
      <div className="space-y-4">
        <ConfigCheckbox
          checked={inventoryConfig.enableWarehouses}
          onChange={(v) => updateConfig('enableWarehouses', v)}
          label={p('enable_warehouses')}
        />
        {prefixField(
          'warehouseReferencePrefix',
          'warehouse_reference_prefix',
          'warehouse_reference_prefix_hint',
          'autoGenerateWarehouseReference',
          'auto_generate_warehouse_reference',
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
          <ConfigCheckbox checked={inventoryConfig.enableMultipleWarehouses} onChange={(v) => updateConfig('enableMultipleWarehouses', v)} label={p('multi_warehouses')} />
          <ConfigCheckbox checked={inventoryConfig.requireLocationTracking} onChange={(v) => updateConfig('requireLocationTracking', v)} label={p('require_location')} />
          <ConfigCheckbox checked={inventoryConfig.enableBarcodeScanning} onChange={(v) => updateConfig('enableBarcodeScanning', v)} label={p('barcode_scanning')} />
        </div>
      </div>
    </ConfigSection>
  );

  const movementsTab = (
    <ConfigSection icon={RefreshCw} title={p('section_movements')} hint={p('section_movements_hint')}>
      <div className="space-y-4">
        <ConfigCheckbox checked={inventoryConfig.enableMovements} onChange={(v) => updateConfig('enableMovements', v)} label={p('enable_movements')} />
        {prefixField(
          'movementReferencePrefix',
          'movement_reference_prefix',
          'movement_reference_prefix_hint',
          'autoGenerateMovementReference',
          'auto_generate_movement_reference',
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <ConfigCheckbox checked={inventoryConfig.auditAllMovements} onChange={(v) => updateConfig('auditAllMovements', v)} label={p('audit_movements')} />
          <ConfigCheckbox checked={inventoryConfig.trackSerialNumbers} onChange={(v) => updateConfig('trackSerialNumbers', v)} label={p('track_serial')} />
        </div>
      </div>
    </ConfigSection>
  );

  const reservationsTab = (
    <ConfigSection icon={Lock} title={p('section_reservations')} hint={p('section_reservations_hint')}>
      <div className="space-y-4">
        <ConfigCheckbox checked={inventoryConfig.enableReservations} onChange={(v) => updateConfig('enableReservations', v)} label={p('enable_reservations')} />
        {prefixField(
          'reservationReferencePrefix',
          'reservation_reference_prefix',
          'reservation_reference_prefix_hint',
          'autoGenerateReservationReference',
          'auto_generate_reservation_reference',
        )}
      </div>
    </ConfigSection>
  );

  const transfersTab = (
    <ConfigSection icon={ArrowRightLeft} title={p('section_transfers')} hint={p('section_transfers_hint')}>
      <div className="space-y-4">
        <ConfigCheckbox checked={inventoryConfig.enableTransfers} onChange={(v) => updateConfig('enableTransfers', v)} label={p('enable_transfers')} />
        {prefixField(
          'transferReferencePrefix',
          'transfer_reference_prefix',
          'transfer_reference_prefix_hint',
          'autoGenerateTransferReference',
          'auto_generate_transfer_reference',
        )}
        <ConfigCheckbox checked={inventoryConfig.requireApprovalForTransfers} onChange={(v) => updateConfig('requireApprovalForTransfers', v)} label={p('require_transfer_approval')} />
      </div>
    </ConfigSection>
  );

  const adjustmentsTab = (
    <ConfigSection icon={AlertTriangle} title={p('section_adjustments')} hint={p('section_adjustments_hint')}>
      <div className="space-y-4">
        <ConfigCheckbox checked={inventoryConfig.enableAdjustments} onChange={(v) => updateConfig('enableAdjustments', v)} label={p('enable_adjustments')} />
        {prefixField(
          'adjustmentReferencePrefix',
          'adjustment_reference_prefix',
          'adjustment_reference_prefix_hint',
          'autoGenerateAdjustmentReference',
          'auto_generate_adjustment_reference',
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <ConfigCheckbox checked={inventoryConfig.restrictStockAdjustments} onChange={(v) => updateConfig('restrictStockAdjustments', v)} label={p('restrict_adjustments')} />
          <ConfigCheckbox checked={inventoryConfig.requireReasonForAdjustment} onChange={(v) => updateConfig('requireReasonForAdjustment', v)} label={p('require_reason')} />
        </div>
      </div>
    </ConfigSection>
  );

  const physicalInventoryTab = (
    <ConfigSection icon={ClipboardCheck} title={p('section_physical_inventory')} hint={p('section_physical_inventory_hint')}>
      <div className="space-y-4">
        <ConfigCheckbox checked={inventoryConfig.enablePhysicalInventory} onChange={(v) => updateConfig('enablePhysicalInventory', v)} label={p('enable_physical_inventory')} />
        {prefixField(
          'physicalInventoryReferencePrefix',
          'physical_inventory_reference_prefix',
          'physical_inventory_reference_prefix_hint',
          'autoGeneratePhysicalInventoryReference',
          'auto_generate_physical_inventory_reference',
        )}
      </div>
    </ConfigSection>
  );

  const expiryTab = (
    <ConfigSection icon={Timer} title={p('section_expiry')} hint={p('section_expiry_hint')}>
      <div className="space-y-4">
        <ConfigCheckbox checked={inventoryConfig.enableExpiryTracking} onChange={(v) => updateConfig('enableExpiryTracking', v)} label={p('enable_expiry_tracking')} />
        {prefixField(
          'lotReferencePrefix',
          'lot_reference_prefix',
          'lot_reference_prefix_hint',
          'autoGenerateLotReference',
          'auto_generate_lot_reference',
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('expiry_alert_days')} hint={p('expiry_alert_days_hint')}>
            <input type="number" min={0} value={inventoryConfig.expiryAlertDays} onChange={(e) => updateConfig('expiryAlertDays', parseInt(e.target.value, 10) || 0)} className={inputClass} />
          </ConfigField>
          <div className="flex flex-col gap-3 justify-center">
            <ConfigCheckbox checked={inventoryConfig.trackBatchNumbers} onChange={(v) => updateConfig('trackBatchNumbers', v)} label={p('track_batch')} />
            <ConfigCheckbox checked={inventoryConfig.trackExpiryDates} onChange={(v) => updateConfig('trackExpiryDates', v)} label={p('track_expiry')} />
            <ConfigCheckbox checked={inventoryConfig.alertExpiringSoon} onChange={(v) => updateConfig('alertExpiringSoon', v)} label={p('alert_expiring')} />
          </div>
        </div>
      </div>
    </ConfigSection>
  );

  const valuationTab = (
    <ConfigSection icon={TrendingUp} title={p('section_valuation')} hint={p('section_valuation_hint')}>
      <div className="space-y-4">
        <ConfigCheckbox checked={inventoryConfig.enableValuation} onChange={(v) => updateConfig('enableValuation', v)} label={p('enable_valuation')} />
        {prefixField(
          'valuationReferencePrefix',
          'valuation_reference_prefix',
          'valuation_reference_prefix_hint',
          'autoGenerateValuationReference',
          'auto_generate_valuation_reference',
        )}
        {qawaem && (
          <p className="text-white/60 text-sm">{p('valuation_saudi_note', { currency: currencySuffix })}</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('valuation_method')} hint={p('valuation_method_hint')}>
            <select value={inventoryConfig.valuationMethod} onChange={(e) => updateConfig('valuationMethod', e.target.value)} className={`custom-select ${inputClass}`}>
              <option value="fifo" className="text-gray-900">{qawaem ? p('fifo_saudi') : p('fifo')}</option>
              {!qawaem && <option value="lifo" className="text-gray-900">{p('lifo')}</option>}
              <option value="average" className="text-gray-900">{p('average_cost')}</option>
            </select>
          </ConfigField>
          <div className="flex flex-col gap-3 justify-center">
            <ConfigCheckbox checked={inventoryConfig.includeShippingInCost} onChange={(v) => updateConfig('includeShippingInCost', v)} label={p('include_shipping')} />
            <ConfigCheckbox checked={inventoryConfig.roundPrices} onChange={(v) => updateConfig('roundPrices', v)} label={p('round_prices')} />
            <ConfigCheckbox checked={inventoryConfig.showStockValue} onChange={(v) => updateConfig('showStockValue', v)} label={p('show_stock_value')} />
          </div>
        </div>
      </div>
    </ConfigSection>
  );

  const reorderTab = (
    <ConfigSection icon={PackageCheck} title={p('section_reorder')} hint={p('section_reorder_hint')}>
      <div className="space-y-4">
        <ConfigCheckbox checked={inventoryConfig.enableReorder} onChange={(v) => updateConfig('enableReorder', v)} label={p('enable_reorder')} />
        {prefixField(
          'reorderReferencePrefix',
          'reorder_reference_prefix',
          'reorder_reference_prefix_hint',
          'autoGenerateReorderReference',
          'auto_generate_reorder_reference',
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('low_stock_threshold')} hint={p('low_stock_threshold_hint')}>
            <input type="number" min={0} value={inventoryConfig.lowStockThreshold} onChange={(e) => updateConfig('lowStockThreshold', parseInt(e.target.value, 10) || 0)} className={inputClass} />
          </ConfigField>
          <ConfigField label={p('critical_stock_threshold')} hint={p('critical_stock_threshold_hint')}>
            <input type="number" min={0} value={inventoryConfig.criticalStockThreshold} onChange={(e) => updateConfig('criticalStockThreshold', parseInt(e.target.value, 10) || 0)} className={inputClass} />
          </ConfigField>
          <ConfigField label={p('reorder_point')} hint={p('reorder_point_hint')}>
            <input type="number" min={0} value={inventoryConfig.reorderPoint} onChange={(e) => updateConfig('reorderPoint', parseInt(e.target.value, 10) || 0)} className={inputClass} />
          </ConfigField>
          <ConfigField label={p('default_lead_time')} hint={p('default_lead_time_hint')}>
            <input type="number" min={0} value={inventoryConfig.defaultLeadTime} onChange={(e) => updateConfig('defaultLeadTime', parseInt(e.target.value, 10) || 0)} className={inputClass} />
          </ConfigField>
          <ConfigField label={p('overstock_threshold')} hint={p('overstock_threshold_hint', { currency: currencySuffix })}>
            <input type="number" min={0} value={inventoryConfig.overstockThreshold} onChange={(e) => updateConfig('overstockThreshold', parseInt(e.target.value, 10) || 0)} className={inputClass} />
          </ConfigField>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <ConfigCheckbox checked={inventoryConfig.autoReorderEnabled} onChange={(v) => updateConfig('autoReorderEnabled', v)} label={p('auto_reorder')} />
          <ConfigCheckbox checked={inventoryConfig.alertLowStock} onChange={(v) => updateConfig('alertLowStock', v)} label={p('alert_low_stock')} />
          <ConfigCheckbox checked={inventoryConfig.alertOverstock} onChange={(v) => updateConfig('alertOverstock', v)} label={p('alert_overstock')} />
        </div>
      </div>
    </ConfigSection>
  );

  const displayTab = (
    <ConfigSection icon={Eye} title={p('section_display')} hint={p('section_display_hint')}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ConfigField label={p('items_per_page')} hint={p('items_per_page_hint')}>
          <select value={inventoryConfig.itemsPerPage} onChange={(e) => updateConfig('itemsPerPage', parseInt(e.target.value, 10))} className={inputClass}>
            {[25, 50, 100].map((n) => (
              <option key={n} value={n} className="text-gray-900">{n}</option>
            ))}
          </select>
        </ConfigField>
        <ConfigField label={p('default_view')} hint={p('default_view_hint')}>
          <select value={inventoryConfig.defaultView} onChange={(e) => updateConfig('defaultView', e.target.value)} className={inputClass}>
            <option value="table" className="text-gray-900">{p('view_table')}</option>
            <option value="cards" className="text-gray-900">{p('view_cards')}</option>
          </select>
        </ConfigField>
      </div>
    </ConfigSection>
  );

  const tabs = [
    { id: 'warehouses', label: p('tab_warehouses'), icon: Warehouse },
    { id: 'movements', label: p('tab_movements'), icon: RefreshCw },
    { id: 'reservations', label: p('tab_reservations'), icon: Lock },
    { id: 'transfers', label: p('tab_transfers'), icon: ArrowRightLeft },
    { id: 'adjustments', label: p('tab_adjustments'), icon: AlertTriangle },
    { id: 'physical', label: p('tab_physical_inventory'), icon: ClipboardCheck },
    { id: 'expiry', label: p('tab_expiry'), icon: Timer },
    { id: 'valuation', label: p('tab_valuation'), icon: TrendingUp },
    { id: 'reorder', label: p('tab_reorder'), icon: PackageCheck },
    { id: 'display', label: p('tab_display'), icon: Shield },
  ];

  const tabContent = {
    warehouses: warehousesTab,
    movements: movementsTab,
    reservations: reservationsTab,
    transfers: transfersTab,
    adjustments: adjustmentsTab,
    physical: physicalInventoryTab,
    expiry: expiryTab,
    valuation: valuationTab,
    reorder: reorderTab,
    display: displayTab,
  };

  return (
    <ModuleConfigPageLayout
      title={p('title')}
      subtitle={p('subtitle')}
      titleIcon={Settings}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      tabContent={tabContent}
      loading={loading}
      saving={saving}
      saved={saved}
      savedMessage={p('saved_success')}
      saveError={saveError}
      onSave={handleSave}
      onReset={handleReset}
    />
  );
};

export default InventoryConfigPage;
